/**
 * Freeform Administration "Other" cost — purpose, description, qty, unit cost.
 * Not a catalog lookup (no materials/services search).
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export interface AddOtherCommitPayload {
  purpose: string;
  description: string;
  quantity: string;
  unitCost: string;
}

export interface AddOtherInlineProps {
  onCommit: (payload: AddOtherCommitPayload) => Promise<void>;
  onCancel: () => void;
}

function isNumericString(s: string): boolean {
  if (s.trim() === '') return false;
  return Number.isFinite(Number(s));
}

function formatMoney(s: string | undefined | null): string {
  if (s == null || s === '') return '—';
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  const hasCents = Math.abs(n % 1) > 0.004;
  return `$ ${n.toLocaleString('en-US', {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
}

export function AddOtherInline({ onCommit, onCancel }: AddOtherInlineProps) {
  const [purpose, setPurpose] = useState('');
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState('');
  const [unitCost, setUnitCost] = useState('');
  const [busy, setBusy] = useState(false);

  const valid =
    purpose.trim() !== '' &&
    description.trim() !== '' &&
    isNumericString(quantity.trim()) &&
    isNumericString(unitCost.trim());

  const lineTotal =
    isNumericString(quantity.trim()) && isNumericString(unitCost.trim())
      ? Number(quantity) * Number(unitCost)
      : null;

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await onCommit({
        purpose: purpose.trim(),
        description: description.trim(),
        quantity: quantity.trim(),
        unitCost: unitCost.trim(),
      });
    } finally {
      setBusy(false);
    }
  };

  const inputClass =
    'h-6 rounded-sm border border-outline-variant/70 bg-muted/45 px-1.5 font-public-sans text-[11px] shadow-none placeholder:text-muted-foreground/80 focus-visible:border-primary focus-visible:bg-card focus-visible:ring-1 focus-visible:ring-ring/40';

  return (
    <form
      className="my-1 border-l-2 border-l-primary/50 py-1 pl-2"
      data-testid="add-other-form"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <p className="mb-1 font-public-sans text-[9px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
        Add Other
      </p>
      <div
        className="flex flex-wrap items-center gap-x-2 gap-y-1 font-public-sans text-[11px]"
        data-testid="add-other-row"
      >
        <Input
          autoFocus
          disabled={busy}
          value={purpose}
          placeholder="Purpose"
          aria-label="Other cost purpose"
          className={`${inputClass} w-[5.5rem] shrink-0`}
          onChange={(e) => setPurpose(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onCancel();
          }}
        />
        <Input
          disabled={busy}
          value={description}
          placeholder="Description"
          aria-label="Other cost description"
          className={`${inputClass} min-w-[12rem] flex-1`}
          onChange={(e) => setDescription(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onCancel();
          }}
        />
        <Input
          disabled={busy}
          value={quantity}
          placeholder="Qty"
          aria-label="Other cost quantity"
          className={`${inputClass} w-12 shrink-0 text-right font-mono`}
          onChange={(e) => setQuantity(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onCancel();
          }}
        />
        <Input
          disabled={busy}
          value={unitCost}
          placeholder="0.00"
          aria-label="Other cost unit cost"
          className={`${inputClass} w-16 shrink-0 text-right font-mono`}
          onChange={(e) => setUnitCost(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onCancel();
          }}
        />
        <span
          className="w-16 shrink-0 text-right font-mono text-[11px] font-semibold tabular-nums text-foreground"
          data-testid="add-other-line-total"
        >
          {lineTotal != null ? formatMoney(String(lineTotal)) : '—'}
        </span>
        <span className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={onCancel}
            className="h-6 px-1.5 py-0 text-[11px] text-muted-foreground hover:text-foreground"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="ghost"
            size="sm"
            disabled={!valid || busy}
            className="h-6 px-1.5 py-0 text-[11px] font-semibold text-primary hover:text-primary-dim disabled:opacity-40"
          >
            Save
          </Button>
        </span>
      </div>
    </form>
  );
}
